import { Fade, Stack, Typography } from '@mui/material';
import { useAuthContext } from 'contexts/AuthContext';
import HourGlass from 'illustrations/HourGlass';
import PageError from 'illustrations/PageError';
import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UnauthorizedReason } from 'types/api';

const UnauthorizedPage: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { authRes, setAuthRes } = useAuthContext();
	const queryReason = searchParams.get('reason');
	const validReasons = useMemo(
		() => Object.values(UnauthorizedReason) as string[],
		[]
	);

	useEffect(() => {
		if (queryReason) {
			if (validReasons.includes(queryReason)) {
				const reason = queryReason as UnauthorizedReason;
				setAuthRes((prev) => ({
					...prev,
					unauthorizedReason: reason
				}));
			}
			searchParams.delete('reason');
			setSearchParams(searchParams, { replace: true });
		}
	}, [queryReason, searchParams, setAuthRes, setSearchParams, validReasons]);

	const finalReason =
		authRes.unauthorizedReason &&
		validReasons.includes(authRes.unauthorizedReason)
			? authRes.unauthorizedReason
			: UnauthorizedReason.NO_AUTH;

	const headingKey = `unauthorizedPage.heading.${finalReason}`;
	const messageKey = `unauthorizedPage.message.${finalReason}`;

	return (
		<Fade in>
			<Stack
				data-testid="unauthorized-page"
				direction="column"
				justifyContent="center"
				alignItems="center"
				height="100%"
			>
				<Typography variant="h2" mb={3}>
					{intl.tr(headingKey)}
				</Typography>
				{finalReason === UnauthorizedReason.LINK_EXPIRED ? (
					<HourGlass data-testid="hour-glass-unauthorized" />
				) : (
					<PageError data-testid="page-error-unauthorized" />
				)}
				<Typography variant="h3" mt={5}>
					{intl.tr(messageKey)}
				</Typography>
			</Stack>
		</Fade>
	);
};

export default UnauthorizedPage;
